import * as THREE from "three";
import { createCloudToonMaterial } from "./materials.js";

/**
 * Toon cloudscape floating above the maze sphere - the puffs themselves are
 * now authored in Blender (a single "Clouds" mesh, instanced across a shell
 * and baked flat for export) rather than generated procedurally here; this
 * module just wires that mesh up to the shared cloud toon shader and
 * animates it.
 *
 * Kept to a single draw call, same as before: the whole shell spins as one
 * rigid mesh, so per-frame cost is a couple of floats, not per-puff work.
 * The per-puff "breathing" bob still comes for free in the vertex shader
 * from an aPhase/aTint attribute pair (see createCloudToonMaterial in
 * materials.js) - the baked mesh doesn't carry those from Blender, so
 * they're synthesized once here, one shared value per puff. Getting the
 * grouping wrong (e.g. a fixed-size spatial grid that happens to split a
 * single puff across two cells) makes different parts of the *same* puff
 * bob out of sync with each other, which reads as that puff deforming
 * rather than floating - so puffs are grouped by real mesh connectivity
 * (each one is a disconnected island of triangles, since Blender's
 * instance-then-join never welds separate instances together) instead of
 * a guessed size threshold.
 */

/** Unions every vertex that shares a triangle with another, using welded
 * (position-equal) vertices as the union-find nodes so it works whether or
 * not the export kept a shared index buffer - hard-edge/UV-seam vertex
 * splitting duplicates positions rather than indices, so matching on
 * position is what actually reconnects a puff's full surface. Each
 * resulting connected component is exactly one puff. */
function computeCloudPuffGroups(geometry) {
  if (!geometry.boundingSphere) geometry.computeBoundingSphere();
  const weldEpsilon = Math.max(geometry.boundingSphere.radius * 1e-5, 1e-6);

  const position = geometry.attributes.position;
  const vertexCount = position.count;
  const posKeyToId = new Map();
  const canonicalId = new Int32Array(vertexCount);
  for (let i = 0; i < vertexCount; i++) {
    const key = `${Math.round(position.getX(i) / weldEpsilon)}_${Math.round(position.getY(i) / weldEpsilon)}_${Math.round(position.getZ(i) / weldEpsilon)}`;
    let id = posKeyToId.get(key);
    if (id === undefined) { id = posKeyToId.size; posKeyToId.set(key, id); }
    canonicalId[i] = id;
  }

  const uniqueCount = posKeyToId.size;
  const parent = new Int32Array(uniqueCount);
  for (let i = 0; i < uniqueCount; i++) parent[i] = i;
  function find(x) {
    while (parent[x] !== x) { parent[x] = parent[parent[x]]; x = parent[x]; }
    return x;
  }
  function union(a, b) {
    const ra = find(a), rb = find(b);
    if (ra !== rb) parent[ra] = rb;
  }

  const index = geometry.index;
  if (index) {
    const tri = index.array;
    for (let i = 0; i < tri.length; i += 3) {
      union(canonicalId[tri[i]], canonicalId[tri[i + 1]]);
      union(canonicalId[tri[i + 1]], canonicalId[tri[i + 2]]);
    }
  } else {
    for (let i = 0; i < vertexCount; i += 3) {
      union(canonicalId[i], canonicalId[i + 1]);
      union(canonicalId[i + 1], canonicalId[i + 2]);
    }
  }

  const rootToComponent = new Map();
  const vertexComponent = new Int32Array(vertexCount);
  for (let i = 0; i < vertexCount; i++) {
    const root = find(canonicalId[i]);
    let componentId = rootToComponent.get(root);
    if (componentId === undefined) { componentId = rootToComponent.size; rootToComponent.set(root, componentId); }
    vertexComponent[i] = componentId;
  }

  return { vertexComponent, componentCount: rootToComponent.size };
}

function assignCloudVertexAttributes(geometry) {
  const { vertexComponent, componentCount } = computeCloudPuffGroups(geometry);

  const componentPhase = new Float32Array(componentCount);
  const componentTint = new Float32Array(componentCount);
  for (let c = 0; c < componentCount; c++) {
    const h1 = Math.sin(c * 12.9898 + 78.233) * 43758.5453;
    componentPhase[c] = (h1 - Math.floor(h1)) * Math.PI * 2;
    const h2 = Math.sin(c * 39.346 + 11.135) * 12543.231;
    componentTint[c] = h2 - Math.floor(h2);
  }

  const vertexCount = geometry.attributes.position.count;
  const phase = new Float32Array(vertexCount);
  const tint = new Float32Array(vertexCount);
  for (let i = 0; i < vertexCount; i++) {
    phase[i] = componentPhase[vertexComponent[i]];
    tint[i] = componentTint[vertexComponent[i]];
  }

  geometry.setAttribute("aPhase", new THREE.Float32BufferAttribute(phase, 1));
  geometry.setAttribute("aTint", new THREE.Float32BufferAttribute(tint, 1));
}

// Wind drift, rad/sec - negative so the shell turns opposite to the maze's
// own "forward" sense (positive theta, see directionToNavCell in
// gameScreen.js) rather than spinning in lockstep with it.
const DRIFT_SPEED_BASE = -0.025;
// The drift speed itself gently rises/falls over time (rather than staying
// perfectly constant) so the shell reads as gusty wind, not a merry-go-round.
const DRIFT_SPEED_VARIATION = 0.012;
const DRIFT_WOBBLE_FREQ = 0.15; // rad/sec - a slow ~40s speed cycle
// Small continuous nod on a second axis, layered on top of the main spin,
// so the shell gently tumbles rather than rotating on a single fixed axis.
const TILT_WOBBLE_AMPLITUDE = THREE.MathUtils.degToRad(4);
const TILT_WOBBLE_FREQ = 0.08; // slower than the speed wobble, for an unsynced drift

export function createClouds({ cloudsMesh }) {
  const material = createCloudToonMaterial();
  cloudsMesh.material = material;
  cloudsMesh.frustumCulled = false; // the shell rings the whole globe - its bounding sphere is basically always on screen anyway
  assignCloudVertexAttributes(cloudsMesh.geometry);

  let elapsed = 0;

  function update(deltaTime) {
    elapsed += deltaTime;
    const speed = DRIFT_SPEED_BASE + DRIFT_SPEED_VARIATION * Math.sin(elapsed * DRIFT_WOBBLE_FREQ);
    cloudsMesh.rotation.y += speed * deltaTime;
    cloudsMesh.rotation.x = TILT_WOBBLE_AMPLITUDE * Math.sin(elapsed * TILT_WOBBLE_FREQ);
    material.uniforms.uTime.value = elapsed;
  }

  return { group: cloudsMesh, update };
}
